"use client";

import { motion } from "framer-motion";
import {
  Mail,
  MapPin,
  Clock,
  ArrowUpRight,
  Share2,
  Camera,
  Send,
} from "lucide-react";
import KontaktForm from "@/components/KontaktForm";
import { PageHeader } from "@/components/shared/PageHeader";
import { EASE } from "@/lib/motion";

const channels = [
  { icon: Share2, label: "Facebook", handle: "@ncrechner", href: "https://www.facebook.com/ncrechner/" },
  { icon: Camera, label: "Instagram", handle: "@ncrechner", href: "https://www.instagram.com/ncrechner/" },
  { icon: Send, label: "Telegram", handle: "@ncrechner", href: "https://msng.link/o/?whos_n=tg" },
];

const infoItems = [
  {
    icon: Mail,
    label: "E-Mail",
    value: "info@ncrechner.de",
    sub: "Direkt in unser Postfach",
    href: "mailto:info@ncrechner.de",
  },
  {
    icon: MapPin,
    label: "Adresse",
    value: "Bettinastraße 62",
    sub: "60325 Frankfurt am Main",
    href: null,
  },
  {
    icon: Clock,
    label: "Antwortzeit",
    value: "1–2 Werktage",
    sub: "Mo–Fr typische Reaktionszeit",
    href: null,
  },
];

export default function KontaktPageContent() {
  return (
    <main className="min-h-screen bg-paper">
      <PageHeader
        divider={false}
        eyebrow="Kontakt"
        title={
          <>
            Komm mit uns in <span className="text-accent">Kontakt</span>
          </>
        }
        subtitle="Du hast Fragen, Feedback oder Verbesserungsvorschläge? Wir freuen uns von dir zu hören und melden uns schnellstmöglich."
      />

      {/* ── Info strip ── */}
      <div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3">
            {infoItems.map(({ icon: Icon, label, value, sub, href }, i) => {
              const inner = (
                <>
                  <Icon size={18} className="text-ink-faint shrink-0" aria-hidden="true" />
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-widest text-ink-faint">
                      {label}
                    </span>
                    <span className="block font-display font-bold text-ink mt-1">{value}</span>
                    <span className="block text-ink-faint text-sm mt-0.5">{sub}</span>
                  </span>
                </>
              );
              const cls =
                "flex items-start gap-4 py-8 lg:px-2";

              return (
                <div
                  key={label}
                  className="enter [--enter-y:16px] [animation-duration:0.5s]"
                  style={{ animationDelay: `${0.2 + i * 0.08}s` }}
                >
                  {href ? (
                    <a href={href} className={`${cls} group cursor-pointer`}>
                      {inner}
                      <ArrowUpRight
                        size={15}
                        className="ml-auto text-ink-faint group-hover:text-accent transition-colors shrink-0"
                        aria-hidden="true"
                      />
                    </a>
                  ) : (
                    <div className={cls}>{inner}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Intro copy ── */}
      <section className="px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: EASE }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <p>
            Ob du eine Frage zur Berechnung deiner Zulassungschancen hast, einen
            Fehler in den Hochschuldaten entdeckt hast oder dir ein neues Feature
            für die ncrechner App wünschst – wir freuen uns über jede Nachricht.
            Unser Team aus Medizinstudierenden kennt das Bewerbungsverfahren aus
            eigener Erfahrung und hilft dir gerne bei allen Fragen rund um die
            Abiturbestenquote, das Auswahlverfahren der Hochschulen (AdH) und die
            zusätzliche Eignungsquote (ZEQ) weiter.
          </p>
          <p>
            Am schnellsten erreichst du uns per E-Mail unter{" "}
            <a
              href="mailto:info@ncrechner.de"
              className="text-accent hover:underline font-semibold"
            >
              info@ncrechner.de
            </a>
            . In der Regel antworten wir innerhalb von ein bis zwei Werktagen.
            Wenn du lieber über Social Media schreibst, findest du uns auf
            Facebook, Instagram und Telegram – dort teilen wir außerdem
            regelmäßig Neuigkeiten, Bewerbungstipps und Updates zur App. Bitte
            beachte, dass wir keine individuelle Rechtsberatung anbieten können;
            bei rechtlichen Fragen zur Anerkennung von Diensten oder Ausbildungen
            vermitteln wir dich aber gerne an passende Ansprechpartner.
          </p>
        </motion.div>
      </section>

      {/* ── Form + sidebar ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-5 gap-14 lg:gap-16 items-start">

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, ease: EASE }}
            className="lg:col-span-3"
          >
            <h2 className="font-display font-bold text-3xl text-ink tracking-tight">
              Nachricht schreiben
            </h2>
            <p className="text-ink-faint text-sm mt-2 mb-8">
              Alle Felder mit <span className="text-accent">*</span> sind Pflichtfelder.
            </p>
            <KontaktForm />
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: 0.1, ease: EASE }}
            className="lg:col-span-2 flex flex-col gap-12"
          >
            {/* Social channels */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-ink-faint mb-2">
                Social Media &amp; Community
              </p>
              {channels.map(({ icon: Icon, label, handle, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 py-4 border-t border-line last:border-b cursor-pointer"
                >
                  <Icon
                    size={17}
                    className="text-ink-faint group-hover:text-accent transition-colors duration-200 shrink-0"
                    aria-hidden="true"
                  />
                  <span className="font-semibold text-sm text-ink group-hover:text-accent transition-colors duration-200">
                    {label}
                  </span>
                  <span className="text-xs text-ink-faint truncate">{handle}</span>
                  <ArrowUpRight
                    size={15}
                    className="ml-auto text-ink-faint group-hover:text-accent group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-200 shrink-0"
                    aria-hidden="true"
                  />
                </a>
              ))}
            </div>

            {/* FAQ teaser */}
            <div className="bg-accent text-paper rounded-2xl p-7">
              <h3 className="font-display font-bold text-xl mb-2">Häufige Fragen?</h3>
              <p className="text-paper/80 text-sm leading-relaxed mb-5">
                Vielleicht findest du deine Antwort direkt in unserem FAQ-Bereich.
              </p>
              <a
                href="/faq/"
                className="inline-flex items-center gap-2 bg-paper text-ink text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-paper-deep transition-colors duration-200 cursor-pointer"
              >
                Zum FAQ
                <ArrowUpRight size={14} aria-hidden="true" />
              </a>
            </div>
          </motion.div>

        </div>
      </section>
    </main>
  );
}
